import { mapValues } from 'lodash';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useAsyncExport } from 'api/async-export/use-async-export';
import { ProjectId } from 'api/models';
import { Action, ObjectType, SocketData } from 'api/pushes';
import { TaskAPI } from 'api/tasks';
import { RootState } from 'data';
import { taskNormalizer } from 'data/tasks/schema';
import { useMultiProjectWsUpdates } from 'hooks/use-multi-project-ws-updates';
import { useUserWsUpdates } from 'hooks/use-user-ws-updates';
import { useProjectPlaceholderWebsocket } from 'hooks/websockets';
import { getProjectDetailQueryKey } from 'queries/common';
import { TaskField } from 'stores/fields';
import { ProjectsStore } from 'stores/projects';
import { created, deleted, Task, updated } from 'stores/tasks';
import { withValidCustomFieldsForData } from 'utils/duplicate-fields-transformer';
import { toStoreTask } from 'utils/to-store-task';
import { unreachable } from 'utils/unreachable';
import { Task as SchemeTask } from '../../../data/tasks';
import { duplicateTasksToStore } from '../duplicate-tasks-to-store';

type TaskSocketData = Pick<Task, 'taskId'>;

interface Option {
  isEnable?: boolean;
  onExportCompleted?: () => void;
  isExportInProgress?: boolean;
}

// @TODO handle indexedDb update here to keep task related changes in sync
export const useAllTasksSocketUpdates = ({
  isEnable = true,
  onExportCompleted = () => {},
  isExportInProgress = false,
}: Option = {}): void => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const projectsStore = useSelector(
    ({ projects_v2 }: RootState) => projects_v2,
  );
  const projectIds = useMemo(
    () =>
      isEnable
        ? ProjectsStore.toList(projectsStore).map(({ projectId }) =>
            ProjectId(projectId),
          )
        : [],
    [isEnable, projectsStore],
  );
  const fieldsStore = useSelector(({ fields }: RootState) => fields);
  const fields_lookup = useMemo(
    () =>
      mapValues(fieldsStore.byId, ([, x]) => x!) as Record<number, TaskField>,
    [fieldsStore.byId],
  );

  const tasksStore = useSelector(({ tasks_v2 }: RootState) => tasks_v2);
  const tasks_lookup = useMemo(
    () => mapValues(tasksStore.byId, ([, x]) => x!) as Record<number, Task>,
    [tasksStore.byId],
  );
  // DON"T CHANGE USE REF HERE. TAKE A LOOK AT https://github.com/Rocketlane/rocket-frontend/pull/4857. ITS PURELY A PERF FIX.
  const to_row = useRef<(normalized_task: SchemeTask) => Task>(
    toStoreTask(fields_lookup, tasks_lookup),
  );

  useEffect(() => {
    to_row.current = toStoreTask(fields_lookup, tasks_lookup);
  }, [fields_lookup, tasks_lookup]);
  const handler = useCallback(
    async ({
      data,
      action,
      requestId,
      projectId,
    }: SocketData<TaskSocketData | number[]>) => {
      if (!isEnable) {
        return;
      }
      if (Array.isArray(data)) {
        duplicateTasksToStore({
          projectId,
          data: data as number[],
          requestId,
          dispatch,
          to_row: to_row.current,
          shouldUpdateTasksInV1Store: true,
        });
        await queryClient.invalidateQueries(
          getProjectDetailQueryKey(ProjectId(projectId)),
        );

        return;
      }
      switch (action) {
        case Action.Create: {
          const task = await TaskAPI.getTask(
            projectId,
            (data as TaskSocketData).taskId,
          );
          const updatedTask = {
            ...task,
            fields: task.fields.map(withValidCustomFieldsForData),
          };
          const {
            entities: {
              tasks: { [data.taskId]: normalized },
            },
          } = taskNormalizer(updatedTask);

          dispatch(created([to_row.current(normalized), requestId]));

          return;
        }
        case Action.Update: {
          const task = await TaskAPI.getTask(projectId, data.taskId);
          const updatedTask = {
            ...task,
            fields: task.fields.map(withValidCustomFieldsForData),
          };
          const {
            entities: {
              tasks: { [data.taskId]: normalized },
            },
          } = taskNormalizer(updatedTask);

          dispatch(updated([to_row.current(normalized)]));

          return;
        }
        case Action.Delete:
          dispatch(deleted([{ taskId: data.taskId }]));

          return;
        default:
          unreachable(action as never);
      }
    },
    [dispatch, isEnable, queryClient],
  );

  const { handleExport } = useAsyncExport({
    onExportCompleted,
    isExportInProgress,
  });

  useMultiProjectWsUpdates(projectIds, handler, [
    Action.Create,
    ObjectType.Task,
  ]);
  useMultiProjectWsUpdates(projectIds, handler, [
    Action.Update,
    ObjectType.Task,
  ]);
  useMultiProjectWsUpdates(projectIds, handler, [
    Action.Delete,
    ObjectType.Task,
  ]);

  useMultiProjectWsUpdates(projectIds, handler, [
    Action.Duplicate,
    ObjectType.Task,
  ]);

  useUserWsUpdates(handleExport, [Action.Create, ObjectType.AsyncProcessor]);

  useProjectPlaceholderWebsocket();
};
